import { Controller } from "@hotwired/stimulus"

// Controls the Home timeline's two-ended year range. The custom handles avoid
// WKWebView's unreliable behavior with overlapping native range inputs.
export default class extends Controller {
  static targets = [
    "sheet", "backdrop", "track", "startHandle", "endHandle", "label", "count",
    "fromInput", "toInput", "apply"
  ]
  static values = {
    earliestYear: Number,
    latestYear: Number,
    fromYear: Number,
    toYear: Number,
    counts: Object
  }

  connect() {
    this._onKeydown = (event) => {
      if (event.key === "Escape" && this.sheetTarget.classList.contains("timeline-filter-sheet--open")) {
        this.close()
      }
    }
    this._onBeforeCache = () => this._hideSheet(false)
    document.addEventListener("keydown", this._onKeydown)
    document.addEventListener("turbo:before-cache", this._onBeforeCache)
    // A prior page can disappear before WKWebView delivers its close message.
    // Home is not an overlay when this controller connects, so reset defensively.
    this._setNativeGestureSuspended(false)
    this.update()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    this._stopDrag()
    this._setNativeGestureSuspended(false)
    document.body.style.overflow = this._previousOverflow || ""
  }

  open() {
    this._previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    this._setNativeGestureSuspended(true)
    this.backdropTarget.classList.add("timeline-filter-backdrop--open")
    this.sheetTarget.classList.add("timeline-filter-sheet--open")
    this.sheetTarget.setAttribute("aria-hidden", "false")
    this.element.querySelector("[data-timeline-filter-open]")?.setAttribute("aria-expanded", "true")
    window.setTimeout(() => this.startHandleTarget.focus(), 220)
  }

  close() {
    this._hideSheet(true)
  }

  apply(event) {
    event.preventDefault()
    const form = event.currentTarget
    const query = new URLSearchParams(new FormData(form))
    const queryString = query.toString()
    const destination = queryString ? `${form.action}?${queryString}` : form.action
    this._hideSheet(false)
    window.Turbo.visit(destination, { action: "replace" })
  }

  beginStartDrag(event) {
    this._beginDrag("start", event)
  }

  beginEndDrag(event) {
    this._beginDrag("end", event)
  }

  beginTrackDrag(event) {
    if (event.target.closest(".timeline-year-picker__handle")) return

    const year = this._yearAt(event.clientX)
    const startDistance = Math.abs(year - this.fromYearValue)
    const endDistance = Math.abs(year - this.toYearValue)
    this._beginDrag(startDistance <= endDistance ? "start" : "end", event)
    this._setActiveYear(year)
  }

  handleStartKey(event) {
    this._handleKey("start", event)
  }

  handleEndKey(event) {
    this._handleKey("end", event)
  }

  update() {
    const span = Math.max(this.latestYearValue - this.earliestYearValue, 1)
    const startProgress = ((this.fromYearValue - this.earliestYearValue) / span) * 100
    const endProgress = ((this.toYearValue - this.earliestYearValue) / span) * 100
    const allTime = this.fromYearValue === this.earliestYearValue &&
      this.toYearValue === this.latestYearValue
    const count = Object.entries(this.countsValue).reduce((total, [year, yearCount]) => {
      const numericYear = Number(year)
      return numericYear >= this.fromYearValue && numericYear <= this.toYearValue
        ? total + Number(yearCount)
        : total
    }, 0)

    this.trackTarget.style.setProperty("--timeline-filter-start", `${startProgress}%`)
    this.trackTarget.style.setProperty("--timeline-filter-end", `${endProgress}%`)
    this.startHandleTarget.style.left = `${startProgress}%`
    this.endHandleTarget.style.left = `${endProgress}%`
    this.startHandleTarget.setAttribute("aria-valuenow", String(this.fromYearValue))
    this.endHandleTarget.setAttribute("aria-valuenow", String(this.toYearValue))
    this.startHandleTarget.setAttribute("aria-valuetext", `First year ${this.fromYearValue}`)
    this.endHandleTarget.setAttribute("aria-valuetext", `Last year ${this.toYearValue}`)
    this.labelTarget.textContent = this._rangeLabel()
    this.countTarget.textContent = `${count.toLocaleString()} ${count === 1 ? "match" : "matches"}`
    this.applyTarget.textContent = allTime ? "Show full timeline" : "Show these matches"
    this.fromInputTarget.disabled = allTime
    this.toInputTarget.disabled = allTime
    this.fromInputTarget.value = allTime ? "" : String(this.fromYearValue)
    this.toInputTarget.value = allTime ? "" : String(this.toYearValue)
  }

  _beginDrag(handle, event) {
    event.preventDefault()
    event.stopPropagation()
    this._stopDrag()
    this._activeHandle = handle
    this._setActiveHandle(handle)
    this._dragMove = (moveEvent) => {
      moveEvent.preventDefault()
      this._setActiveYear(this._yearAt(moveEvent.clientX))
    }
    this._dragEnd = () => this._stopDrag()
    window.addEventListener("pointermove", this._dragMove, { passive: false })
    window.addEventListener("pointerup", this._dragEnd, { once: true })
    window.addEventListener("pointercancel", this._dragEnd, { once: true })
  }

  _stopDrag() {
    if (this._dragMove) window.removeEventListener("pointermove", this._dragMove)
    if (this._dragEnd) {
      window.removeEventListener("pointerup", this._dragEnd)
      window.removeEventListener("pointercancel", this._dragEnd)
    }
    this._dragMove = null
    this._dragEnd = null
    this._activeHandle = null
  }

  _setActiveYear(year) {
    if (this._activeHandle === "start") {
      this.fromYearValue = Math.min(year, this.toYearValue)
    } else {
      this.toYearValue = Math.max(year, this.fromYearValue)
    }
    this.update()
  }

  _yearAt(clientX) {
    const rect = this.trackTarget.getBoundingClientRect()
    const ratio = Math.min(Math.max((clientX - rect.left) / Math.max(rect.width, 1), 0), 1)
    return Math.round(this.earliestYearValue + ratio * (this.latestYearValue - this.earliestYearValue))
  }

  _handleKey(handle, event) {
    let delta = 0
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") delta = -1
    if (event.key === "ArrowRight" || event.key === "ArrowUp") delta = 1

    if (event.key === "Home") {
      event.preventDefault()
      this._activeHandle = handle
      this._setActiveYear(this.earliestYearValue)
      return
    }
    if (event.key === "End") {
      event.preventDefault()
      this._activeHandle = handle
      this._setActiveYear(this.latestYearValue)
      return
    }
    if (!delta) return

    event.preventDefault()
    this._activeHandle = handle
    this._setActiveHandle(handle)
    const current = handle === "start" ? this.fromYearValue : this.toYearValue
    this._setActiveYear(Math.min(Math.max(current + delta, this.earliestYearValue), this.latestYearValue))
  }

  _setActiveHandle(handle) {
    this.startHandleTarget.style.zIndex = handle === "start" ? "4" : "3"
    this.endHandleTarget.style.zIndex = handle === "end" ? "4" : "3"
  }

  _rangeLabel() {
    if (this.fromYearValue === this.earliestYearValue && this.toYearValue === this.latestYearValue) return "All time"
    if (this.fromYearValue === this.earliestYearValue) return `Through ${this.toYearValue}`
    if (this.toYearValue === this.latestYearValue) return `Since ${this.fromYearValue}`
    if (this.fromYearValue === this.toYearValue) return String(this.fromYearValue)
    return `${this.fromYearValue} to ${this.toYearValue}`
  }

  _hideSheet(restoreFocus) {
    this._stopDrag()
    this._setNativeGestureSuspended(false)
    document.body.style.overflow = this._previousOverflow || ""
    this.backdropTarget.classList.remove("timeline-filter-backdrop--open")
    this.sheetTarget.classList.remove("timeline-filter-sheet--open")
    this.sheetTarget.setAttribute("aria-hidden", "true")
    const opener = this.element.querySelector("[data-timeline-filter-open]")
    opener?.setAttribute("aria-expanded", "false")
    if (restoreFocus) opener?.focus()
  }

  _setNativeGestureSuspended(suspended) {
    window.webkit?.messageHandlers?.nativeOverlay?.postMessage(suspended)
  }
}
